import React from "react";

const BoxIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="rgb(35, 180, 85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z" />
    <path d="m3.3 7 8.7 5 8.7-5" />
    <path d="M12 22V12" />
  </svg>
);

const CheckCircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="rgb(35, 180, 85)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <path d="m9 11 3 3L22 4" />
  </svg>
);

const BOX_ITEMS = [
  "Verycon Compact Controller",
  "4.3\" Touch Screen Graphic Display",
  "LCD-to-Board Cable",
  "RFID Reader with 8-pin Cable",
  "RFID Card",
  "RGB LED Strip",
  "LED Cable",
  "Emergency Stop Button",
  "Emergency NC Terminal",
  "Emergency Stop Button Sticker",
];

const WHO_NOT_FOR_ITEMS= [
  "EV Charging Startups",
  "EV Charger Manufacturers",
  "System Integrators",
  "R&D Teams",
];

const  WHO_FOR_ITEMS  = ["Training and Educational Institutes", "EV Charging Startups",
  "EV Charger Manufacturers",
  "System Integrators",
  "R&D Teams",];


// Reusable CTA button — same visual style everywhere, content/link differs per usage
function CtaButton({ href = "#", children, className = "" }) {
  return (
    <a href={href} className={`inline-block mt-6 w-full ${className}`}>
      <div className="bg-[rgb(35,180,85)] bg-opacity-5 backdrop-blur-sm border border-[rgb(35,180,85)] border-opacity-30 rounded-xl px-6 py-4 text-center group hover:bg-opacity-10 hover:shadow-md transition-all duration-300">
        <span className="font-poppins font-semibold text-[rgb(35,180,85)] inline-block group-hover:translate-x-1 transition-transform">
          {children}
        </span>
      </div>
    </a>
  );
}

function InfoCard({ icon, iconBg, title, items, ItemIcon, footer, isCompact = false }) {
  return (
    <div className="rounded-2xl border border-[rgb(35,180,85)] shadow-[0_0_15px_rgba(0,0,0,0.06)] p-6 flex flex-col h-full bg-white">
      <div className="flex items-center gap-3 mb-5">
        <span
          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
          style={{ backgroundColor: iconBg }}
        >
          {icon}
        </span>
        <p className="font-bold text-2xl font-gilroy">{title}</p>
      </div>

      <ul className={`space-y-3 ${!isCompact ? 'flex-1' : ''}`}>
        {items.map((item) => (
          <li key={item} className="flex items-start gap-2">
            <ItemIcon className="w-4 h-4 shrink-0 mt-1" />
            <span className="font-poppins text-black/80 text-lg">{item}</span>
          </li>
        ))}
      </ul>

      {footer}
    </div>
  );
}

// Third box (last column): standalone CTA card — title, paragraph, button
function CtaCard({ title, paragraph, buttonHref, buttonLabel }) {
  return (
    <div className="rounded-2xl border border-[rgb(35,180,85)] shadow-[0_0_15px_rgba(0,0,0,0.06)] p-8 h-full flex flex-col justify-center bg-[rgb(35,180,85)] bg-opacity-[0.04]">
      <p className="font-bold text-2xl font-gilroy mb-3 leading-tight">{title}</p>
      <p className="font-poppins text-black/70 text-lg leading-relaxed">
        {paragraph}
      </p>
      <CtaButton href={buttonHref}>{buttonLabel}</CtaButton>
    </div>
  );
}

export default function ProductBoxAndAudience({
  boxItems = BOX_ITEMS,
  whoForItems = WHO_FOR_ITEMS,
  wrapInContainer = true,

  // Third box (last column): standalone CTA card
  ctaCardTitle = "Ready to Start Your Training?",
  ctaCardParagraph = "Get hands-on industry training with expert guidance and the right tools to build your skills.",
  ctaCardHref = "/get-in-touch#contact-us",
  ctaCardButtonLabel = "Join Our Paid Training Program →",
}) {
  const content = (
    <div className="grid md:grid-cols-3 gap-6 items-stretch">
      <InfoCard
        icon={<BoxIcon className="w-5 h-5" />}
        iconBg="rgba(35, 180, 85, 0.12)"
        title="What's in the Box"
        items={boxItems}
        ItemIcon={BoxIcon}
      />

      <InfoCard
        icon={<CheckCircleIcon className="w-5 h-5" />}
        iconBg="rgba(35, 180, 85, 0.12)"
        title="Who is it For"
        items={whoForItems}
        ItemIcon={CheckCircleIcon}
      />

      {/* Third and last box: the CTA card */}
      <CtaCard
        title={ctaCardTitle}
        paragraph={ctaCardParagraph}
        buttonHref={ctaCardHref}
        buttonLabel={ctaCardButtonLabel}
      />
    </div>
  );

  return wrapInContainer ? (
    <div className="containers-min mx-auto px-4 py-12">{content}</div>
  ) : (
    content
  );
}